import api from './api';

// ==================== Platform Settings ====================

// STT Engine Configuration - Full configuration for dynamic STT engine management
export interface STTEngineConfig {
  id: string;                          // Unique identifier (e.g., 'deepgram', 'telnyx')
  name: string;                        // Display name (e.g., 'Deepgram Nova-2')
  provider: string;                    // Provider name (deepgram, telnyx, google, assemblyai)
  description: string;                 // Description for admins
  isActive: boolean;                   // Whether engine is active/available

  // Provider-specific configuration
  config: {
    apiKeyConfigured: boolean;         // Whether API key is configured (don't expose actual key)
    endpoint?: string;                 // Custom endpoint URL if applicable
    model?: string;                    // Default model (e.g., 'nova-2' for Deepgram)
  };

  // Supported features
  features: {
    punctuation: boolean;
    diarization: boolean;
    interimResults: boolean;
    wordTimestamps: boolean;
    customVocabulary: boolean;
    smartFormat: boolean;
  };

  // Supported languages
  supportedLanguages: string[];

  // Cost information
  costTier: 'low' | 'medium' | 'high';
  pricePerMinute?: number;

  // Metadata
  createdAt?: string;
  updatedAt?: string;
}

// AI Model Configuration - Full configuration for dynamic model management
export interface AIModelConfig {
  id: string;                          // Unique identifier
  name: string;                        // Display name
  provider: 'google' | 'anthropic';    // Provider (both use Vertex AI)
  model: string;                       // Model ID (e.g., 'gemini-2.5-flash', 'claude-sonnet-4-20250514')
  description: string;                 // Description for admins
  isActive: boolean;                   // Whether model is active/available

  // Model parameters
  parameters: {
    temperature: number;               // 0.0 - 2.0
    maxOutputTokens: number;           // Max tokens in response
    topP?: number;                     // Nucleus sampling (0.0 - 1.0)
    topK?: number;                     // Top-k sampling
  };

  // Vertex AI configuration
  vertexAI: {
    projectId: string;                 // GCP project ID
    location: string;                  // e.g., 'us-central1'
  };

  // Capabilities and restrictions (for bot orchestration)
  capabilities: ('orchestration' | 'sentiment' | 'summarization' | 'function_calling')[];
  costTier: 'low' | 'medium' | 'high';

  // Usage limits (optional)
  limits?: {
    maxRequestsPerMinute?: number;
    maxTokensPerDay?: number;
  };

  // Metadata
  createdAt?: string;
  updatedAt?: string;
}

// Flow Architect Configuration - AI-powered flow builder settings
export interface FlowArchitectConfig {
  enabled: boolean;                      // Whether Flow Architect is enabled
  llmIntegrationId: string | null;       // UUID of LLM integration to use
  model: string | null;                  // Model override (optional)
  temperature: number;                   // 0.0 - 1.0
  maxTokens: number;                     // Max output tokens
  allowTenantOverride: boolean;          // Allow tenants to choose their own LLM
}

// Storage Provider Configuration (for document sources)
export interface StorageProviderConfig {
  id: string;
  name: string;
  provider: 'sharepoint' | 'google_drive' | 'dropbox' | 'onedrive' | 'box' | 's3' | 'azure_blob';
  description: string;
  isActive: boolean;
  // Provider-specific config is stored encrypted in backend
  configuredFields: string[];  // Which fields have been configured (without values)
}

export interface PlatformSettings {
  platformName: string;
  /**
   * Platform slug — short URL-safe identifier (e.g., "adaptcx") used to
   * build platform-scope mail subdomains (`{slug}.adaptcx.ai` or
   * `{slug}.{env}.adaptcx.ai`) and to identify the platform on inbound
   * webhook routing. Must be unique across all Tenant.code values too.
   */
  slug: string;
  supportEmail: string;
  defaultTimezone: string;
  documentationUrl: string;
  maintenanceMode: boolean;
  allowNewPartners: boolean;
  allowNewTenants: boolean;
  maxTenantsPerPartner: number;
  defaultTenantTier: string;
  trialDays: number;
  enableAI: boolean;
  enableRecordings: boolean;
  retentionDays: number;
  // Security settings
  mfaRequired: boolean;
  ipAllowlistEnabled: boolean;
  ipAllowlist: string[];
  auditLogging: boolean;
  sessionTimeoutMinutes: number;
  passwordExpiryDays: number;
  // AI settings
  ai: {
    defaultModel: string;
    availableModels: AIModelConfig[];
  };
  // STT settings
  stt: {
    defaultEngine: string;
    availableEngines: STTEngineConfig[];
  };
  // Storage settings (for KB document sources)
  storage: {
    availableProviders: StorageProviderConfig[];
  };
  // Flow Architect settings
  flowArchitect: FlowArchitectConfig;
}

// ==================== Tenant Settings ====================

export interface TenantSettings {
  // General
  organizationName: string;
  timezone: string;
  dateFormat: string;
  language: string;
  // Notifications
  notifications: {
    emailNotifications: boolean;
    hearingReminders: boolean;
    systemAlerts: boolean;
    weeklyReports: boolean;
    reminderHours: number;
  };
  // Security
  security: {
    mfaRequired: boolean;
    sessionTimeout: number;
    passwordExpiry: number;
    auditLogging: boolean;
    ipAllowlistEnabled: boolean;
    ipAllowlist: string[];
  };
  // Hearings
  hearings: {
    defaultDuration: number;
    bufferTime: number;
    maxParticipants: number;
    recordingEnabled: boolean;
    transcriptionEnabled: boolean;
    transcriptionLanguage: string;
    transcriptionEngine: string;
    transcriptionResultType: 'final' | 'interim';  // final = only complete sentences, interim = real-time partial results
    autoReminders: boolean;
    holdMusicUrl?: string;
    dialOutNumber?: string;
    earlyJoinMinutes: number;  // How early participants can dial in before scheduled time
    gracePeriodMinutes: number;  // How long after scheduled end to allow dial-in/rejoin
    inboundIvr?: {
      enabled: boolean;
      bridgeNumbers?: string[];  // Multiple dial-in numbers supported
      welcome: { useAudio: boolean; audioUrl?: string; text: string };
      hearingCodePrompt: { useAudio: boolean; audioUrl?: string; text: string };
      participantCodePrompt: { useAudio: boolean; audioUrl?: string; text: string };
      invalidCodePrompt: { useAudio: boolean; audioUrl?: string; text: string };
      holdMessage: { useAudio: boolean; audioUrl?: string; text: string };
      officerWelcome: { useAudio: boolean; audioUrl?: string; text: string };
      disclaimer: {
        enabled: boolean;
        playTiming: string;
        useAudio: boolean;
        audioUrl?: string;
        text: string;
      };
    };
  };
  // AI settings (for bot orchestration)
  ai: {
    enabled: boolean;
    selectedModel: string;  // Default model ID from platform's available models
    allowedModels: string[]; // Model IDs this tenant can use (set by super admin)
    features: {
      botOrchestration: boolean;    // Enable AI-powered bot conversations
      sentimentAnalysis: boolean;   // Enable sentiment detection in conversations
      summarization: boolean;       // Enable conversation summarization
    };
  };
  // DID (Direct Inward Dialing) call settings - for ad-hoc calls outside hearings
  did?: {
    holdMusicUrl?: string;           // Hold music URL for putting callers on hold
    ringtoneUrl?: string;            // Ringtone URL for incoming calls in officer's browser
    maxParticipants: number;         // Max participants in conference
    recordingEnabled: boolean;       // Enable call recording
    beepEnabled: boolean;            // Play beep when participants join/leave
    comfortNoise: boolean;           // Play comfort noise during silence
    endConferenceOnExit: boolean;    // End conference when officer leaves
  };
  // Legal Research settings
  legalResearch?: {
    enabled: boolean;                // Enable legal research feature
    useStreaming: boolean;           // Use streaming (SSE) vs standard HTTP for responses
    cacheTtlMinutes: number;         // Cache identical queries for this many minutes (0 = disabled)
    minScoreStatutes: number;        // Minimum relevance score for statutes (0.0 - 1.0)
    minScoreCases: number;           // Minimum relevance score for cases (0.0 - 1.0)
    topKStatutes: number;            // Number of statutes to retrieve
    topKCases: number;               // Number of similar cases to retrieve
    maxContextTokens: number;        // Max tokens for RAG context
  };
}

// ==================== Partner Settings ====================

export interface PartnerSettings {
  companyName: string;
  supportEmail: string;
  maxTenants: number;
  allowSelfProvisioning: boolean;
  defaultTenantTier: string;
  brandingEnabled: boolean;
  security: {
    sessionTimeout: number;
    mfaRequired: boolean;
    ipRestriction: boolean;
    allowedIPs: string[];
  };
}

// ==================== Defaults ====================

const defaultPlatformSettings: PlatformSettings = {
  platformName: 'AdaptCX',
  slug: 'adaptcx',
  supportEmail: 'support@adaptcx.ai',
  defaultTimezone: 'America/New_York',
  documentationUrl: '',
  maintenanceMode: false,
  allowNewPartners: true,
  allowNewTenants: true,
  maxTenantsPerPartner: 50,
  defaultTenantTier: 'Professional',
  trialDays: 14,
  enableAI: true,
  enableRecordings: true,
  retentionDays: 365,
  // Security defaults
  mfaRequired: true,
  ipAllowlistEnabled: false,
  ipAllowlist: [],
  auditLogging: true,
  sessionTimeoutMinutes: 30,
  passwordExpiryDays: 90,
  // AI defaults (for bot orchestration)
  ai: {
    defaultModel: 'gemini-2.5-flash',
    availableModels: [
      {
        id: 'gemini-2.5-pro',
        name: 'Gemini 2.5 Pro',
        provider: 'google',
        model: 'gemini-2.5-pro',
        description: 'Google\'s most capable model for complex reasoning and orchestration',
        isActive: true,
        parameters: {
          temperature: 0.7,
          maxOutputTokens: 8192,
          topP: 0.95,
        },
        vertexAI: {
          projectId: '',
          location: 'us-central1',
        },
        capabilities: ['orchestration', 'sentiment', 'summarization', 'function_calling'],
        costTier: 'high',
      },
      {
        id: 'gemini-2.5-flash',
        name: 'Gemini 2.5 Flash',
        provider: 'google',
        model: 'gemini-2.5-flash',
        description: 'Fast and efficient for high-volume bot conversations',
        isActive: true,
        parameters: {
          temperature: 0.7,
          maxOutputTokens: 8192,
          topP: 0.95,
        },
        vertexAI: {
          projectId: '',
          location: 'us-central1',
        },
        capabilities: ['orchestration', 'sentiment', 'summarization', 'function_calling'],
        costTier: 'medium',
      },
      {
        id: 'gemini-2.5-flash-lite',
        name: 'Gemini 2.5 Flash-Lite',
        provider: 'google',
        model: 'gemini-2.5-flash-lite',
        description: 'Cost-effective model for simple bot interactions',
        isActive: true,
        parameters: {
          temperature: 0.7,
          maxOutputTokens: 8192,
          topP: 0.95,
        },
        vertexAI: {
          projectId: '',
          location: 'us-central1',
        },
        capabilities: ['orchestration', 'sentiment', 'summarization'],
        costTier: 'low',
      },
      {
        id: 'claude-sonnet-4',
        name: 'Claude Sonnet 4',
        provider: 'anthropic',
        model: 'claude-sonnet-4-20250514',
        description: 'Anthropic\'s latest model for complex bot orchestration',
        isActive: false,
        parameters: {
          temperature: 0.7,
          maxOutputTokens: 8192,
          topP: 0.95,
        },
        vertexAI: {
          projectId: '',
          location: 'us-east5',
        },
        capabilities: ['orchestration', 'sentiment', 'summarization', 'function_calling'],
        costTier: 'high',
      },
      {
        id: 'claude-3.5-sonnet',
        name: 'Claude 3.5 Sonnet',
        provider: 'anthropic',
        model: 'claude-3-5-sonnet@20241022',
        description: 'Anthropic\'s balanced model for bot conversations',
        isActive: false,
        parameters: {
          temperature: 0.7,
          maxOutputTokens: 8192,
          topP: 0.95,
        },
        vertexAI: {
          projectId: '',
          location: 'us-east5',
        },
        capabilities: ['orchestration', 'sentiment', 'summarization', 'function_calling'],
        costTier: 'medium',
      },
    ],
  },
  // STT defaults
  stt: {
    defaultEngine: 'telnyx',
    availableEngines: [
      {
        id: 'telnyx',
        name: 'Telnyx Built-in',
        provider: 'telnyx',
        description: 'Default built-in transcription via Telnyx',
        isActive: true,
        config: {
          apiKeyConfigured: true,  // Uses Telnyx API key
        },
        features: {
          punctuation: true,
          diarization: false,
          interimResults: true,
          wordTimestamps: false,
          customVocabulary: false,
          smartFormat: false,
        },
        supportedLanguages: ['en', 'es', 'fr', 'de', 'it', 'pt'],
        costTier: 'low',
      },
      {
        id: 'deepgram',
        name: 'Deepgram Nova-2',
        provider: 'deepgram',
        description: 'High-accuracy speech recognition with fast processing',
        isActive: false,
        config: {
          apiKeyConfigured: false,
          model: 'nova-2',
        },
        features: {
          punctuation: true,
          diarization: true,
          interimResults: true,
          wordTimestamps: true,
          customVocabulary: true,
          smartFormat: true,
        },
        supportedLanguages: ['en', 'es', 'fr', 'de', 'it', 'pt', 'nl', 'ja', 'ko', 'zh'],
        costTier: 'medium',
      },
      {
        id: 'google',
        name: 'Google Speech-to-Text',
        provider: 'google',
        description: 'Google Cloud Speech-to-Text v2 with phone call optimization',
        isActive: false,
        config: {
          apiKeyConfigured: false,
          model: 'phone_call',
        },
        features: {
          punctuation: true,
          diarization: true,
          interimResults: true,
          wordTimestamps: true,
          customVocabulary: true,
          smartFormat: false,
        },
        supportedLanguages: ['en', 'es', 'fr', 'de', 'it', 'pt', 'nl', 'ja', 'ko', 'zh', 'ru', 'ar', 'hi'],
        costTier: 'medium',
      },
      {
        id: 'assemblyai',
        name: 'AssemblyAI',
        provider: 'assemblyai',
        description: 'AssemblyAI real-time transcription with speaker diarization',
        isActive: false,
        config: {
          apiKeyConfigured: false,
        },
        features: {
          punctuation: true,
          diarization: true,
          interimResults: true,
          wordTimestamps: true,
          customVocabulary: false,
          smartFormat: true,
        },
        supportedLanguages: ['en', 'es', 'fr', 'de', 'it', 'pt', 'nl'],
        costTier: 'medium',
      },
    ],
  },
  // Storage defaults (for KB document sources)
  storage: {
    availableProviders: [
      {
        id: 'sharepoint-default',
        name: 'SharePoint',
        provider: 'sharepoint',
        description: 'Microsoft SharePoint document libraries',
        isActive: true,
        configuredFields: [],
      },
      {
        id: 'google-drive-default',
        name: 'Google Drive',
        provider: 'google_drive',
        description: 'Google Drive folders and files',
        isActive: true,
        configuredFields: [],
      },
      {
        id: 'dropbox-default',
        name: 'Dropbox',
        provider: 'dropbox',
        description: 'Dropbox cloud storage',
        isActive: true,
        configuredFields: [],
      },
      {
        id: 'onedrive-default',
        name: 'OneDrive',
        provider: 'onedrive',
        description: 'Microsoft OneDrive storage',
        isActive: true,
        configuredFields: [],
      },
      {
        id: 'box-default',
        name: 'Box',
        provider: 'box',
        description: 'Box cloud content management',
        isActive: true,
        configuredFields: [],
      },
      {
        id: 's3-default',
        name: 'Amazon S3',
        provider: 's3',
        description: 'AWS S3 bucket storage',
        isActive: true,
        configuredFields: [],
      },
      {
        id: 'azure-blob-default',
        name: 'Azure Blob Storage',
        provider: 'azure_blob',
        description: 'Azure Blob Storage container',
        isActive: true,
        configuredFields: [],
      },
    ],
  },
  // Flow Architect defaults
  flowArchitect: {
    enabled: true,
    llmIntegrationId: null,
    model: null,
    temperature: 0.7,
    maxTokens: 4096,
    allowTenantOverride: true,
  },
};

const defaultTenantSettings: TenantSettings = {
  organizationName: '',
  timezone: 'America/New_York',
  dateFormat: 'MM/DD/YYYY',
  language: 'en-US',
  notifications: {
    emailNotifications: true,
    hearingReminders: true,
    systemAlerts: true,
    weeklyReports: true,
    reminderHours: 24,
  },
  security: {
    mfaRequired: false,
    sessionTimeout: 30,
    passwordExpiry: 90,
    auditLogging: true,
    ipAllowlistEnabled: false,
    ipAllowlist: [],
  },
  hearings: {
    defaultDuration: 30,
    bufferTime: 15,
    maxParticipants: 10,
    recordingEnabled: true,
    transcriptionEnabled: true,
    transcriptionLanguage: 'en',
    transcriptionEngine: 'telnyx',
    transcriptionResultType: 'final',
    autoReminders: true,
    holdMusicUrl: undefined,
    dialOutNumber: undefined,
    earlyJoinMinutes: 15,
    gracePeriodMinutes: 30,
    inboundIvr: undefined,
  },
  ai: {
    enabled: true,
    selectedModel: '',
    allowedModels: [], // Populated by super admin when assigning models to tenant
    features: {
      botOrchestration: true,
      sentimentAnalysis: true,
      summarization: true,
    },
  },
  did: {
    holdMusicUrl: undefined,
    ringtoneUrl: undefined,
    maxParticipants: 10,
    recordingEnabled: false,
    beepEnabled: true,
    comfortNoise: true,
    endConferenceOnExit: true,
  },
  legalResearch: {
    enabled: true,
    useStreaming: true,
    cacheTtlMinutes: 10,
    minScoreStatutes: 0.5,
    minScoreCases: 0.5,
    topKStatutes: 5,
    topKCases: 5,
    maxContextTokens: 2000,
  },
};

const defaultPartnerSettings: PartnerSettings = {
  companyName: '',
  supportEmail: '',
  maxTenants: 50,
  allowSelfProvisioning: true,
  defaultTenantTier: 'Professional',
  brandingEnabled: true,
  security: {
    sessionTimeout: 30,
    mfaRequired: false,
    ipRestriction: false,
    allowedIPs: [],
  },
};

// ==================== IVR Profile ====================

export interface IvrProfile {
  id: string;
  tenant_id: string;
  name: string;
  description?: string;
  flow?: Record<string, unknown>;
  is_active: boolean;
  is_default: boolean;
  created_at: string;
  updated_at?: string;
}

// ==================== Platform MFA Settings Types ====================

export interface PlatformMfaSettings {
  totp: {
    enabled: boolean;
  };
  email: {
    enabled: boolean;
    from_email: string | null;
    from_name: string | null;
  };
  sms: {
    enabled: boolean;
    from_number: string | null;
  };
}

// ==================== Platform Embedding Settings Types ====================

export interface PlatformEmbeddingSettings {
  id?: string;
  // Embedding Model
  embedding_provider: string;
  embedding_model: string;
  embedding_location: string;
  embedding_dimensions: number;
  // Task Types
  task_type_document: string;
  task_type_query: string;
  // Processing Settings
  batch_size: number;
  max_text_length: number;
  // Chunking Settings
  chunk_size_tokens: number;
  chunk_overlap_tokens: number;
  chunk_min_length: number;
  // Search Settings
  search_top_k: number;
  search_min_score: number;
  context_max_tokens: number;
  // Metadata
  updated_by?: string;
  updated_at?: string;
  is_active?: boolean;
}

const defaultPlatformMfaSettings: PlatformMfaSettings = {
  totp: { enabled: true },
  email: { enabled: true, from_email: null, from_name: null },
  sms: { enabled: false, from_number: null },
};

// ==================== Platform RAG Search Settings Types ====================
// S-00 — super-admin toggles for reranker / MMR / hybrid / parent-doc.

export interface PlatformRagSearchSettings {
  id?: string;
  is_active?: boolean;
  // Reranker — `rerank_threshold` is DEPRECATED by v2.2.39's gap-based
  // skip (the four `*_norm` fields below). Field kept on the response
  // shape during the transition window; va/v2.3.90 ignores it.
  rerank_threshold: number;
  // v2.2.39 — gap-based rerank skip thresholds on normalized RRF (0-1).
  // Skip rerank when top_norm >= rerank_skip_top_norm AND gap >= rerank_skip_gap_norm.
  // Force rerank when top_norm < rerank_force_low_top_norm OR gap < rerank_force_small_gap_norm.
  rerank_skip_top_norm: number;
  rerank_skip_gap_norm: number;
  rerank_force_low_top_norm: number;
  rerank_force_small_gap_norm: number;
  // MMR diversity
  mmr_enabled: boolean;
  mmr_lambda: number;
  // Hybrid (BM25 + vector)
  hybrid_search_enabled: boolean;
  hybrid_vector_weight: number;
  // Parent-doc retrieval
  parent_doc_enabled: boolean;
  parent_doc_window: number;
  // v2.2.40 — Phase 3: blend weights + final-context trim + source-count gap.
  // `final_score = blend_w_ret × retrieval + blend_w_rerank × rerank_score`.
  // Defaults 0.0 / 1.0 → order by rerank_score alone.
  blend_w_ret: number;
  blend_w_rerank: number;
  // Top-N chunks sent to the answer LLM after blend/sort.
  final_context_k: number;
  // Top-two gap (on `final_score`) below which the consumer surfaces a 2nd source.
  source_second_gap: number;
  // v2.2.43 — platform-wide score_label thresholds. Drive the
  // "Top match / Match / Related" chip text in bot widget +
  // agent-assist-widget + agent-assist-portal source cards.
  // Platform-only — no per-tenant override.
  score_label_top_match_min: number;
  score_label_match_min: number;
  // v2.2.45 — when true, retrieval multiplies final_score by each
  // chunk's chunk_quality_score to down-rank boilerplate. Default
  // false — eval data validates per-platform before flip.
  use_chunk_quality_score: boolean;
  // v2.2.46 — when true (default), context assembly collapses chunks
  // that share a `table_id` (XLSX rows, MD inline tables, PDF page
  // tables) into one shared header block with per-row citations,
  // instead of repeating the source header N times. Per-source
  // widget payload is unchanged; pure LLM-prompt compression.
  group_table_chunks_in_context: boolean;
  // Audit
  updated_by?: string;
  updated_at?: string;
}

const defaultPlatformRagSearchSettings: PlatformRagSearchSettings = {
  rerank_threshold: 0.8,
  // v2.2.39 — first-principles defaults on a normalized-RRF 0-1 scale.
  rerank_skip_top_norm: 0.30,
  rerank_skip_gap_norm: 0.10,
  rerank_force_low_top_norm: 0.15,
  rerank_force_small_gap_norm: 0.05,
  mmr_enabled: true,
  mmr_lambda: 0.7,
  hybrid_search_enabled: true,
  hybrid_vector_weight: 0.7,
  parent_doc_enabled: false,
  parent_doc_window: 1,
  // v2.2.40 — Phase 3 defaults.
  blend_w_ret: 0.0,
  blend_w_rerank: 1.0,
  final_context_k: 5,
  source_second_gap: 0.08,
  // v2.2.43 — score_label thresholds. Match the prior hardcoded constants.
  score_label_top_match_min: 0.50,
  score_label_match_min: 0.30,
  // v2.2.45 — quality-score retrieval toggle (default off until eval-validated).
  use_chunk_quality_score: false,
  // v2.2.46 — table-chunk grouping in LLM context (default on; strictly compressive).
  group_table_chunks_in_context: true,
};

// ==================== Tenant RAG Settings (S-04) ====================
// Per-tenant reranker config, stored under `tenants.settings.rag`. Consumed
// at runtime by `adaptcx_va.rag.search_service._get_tenant_rag_settings`.
//
// Role split:
//   * super-admin writes `reranker_enabled` only
//     (via PATCH /tenants/{id}/settings).
//   * tenant-admin writes `reranker_provider` + `reranker_integration_id` +
//     `reranker_apply_bot` + `reranker_apply_assist` only
//     (via PATCH /tenants/me/settings). Disallowed fields are silently dropped
//     by the backend, so it's safe to send a partial payload here.
//
// v2.2.38 — `llm_judge` provider dropped. Vertex Ranking is the only
// non-`off` reranker provider. `reranker_allowed_providers` allowlist
// removed (was vestigial with a single provider).

export type TenantRerankerProvider = 'off' | 'vertex_rank';

export interface TenantRagSettings {
  reranker_enabled: boolean;
  reranker_provider: TenantRerankerProvider;
  /** TenantIntegrationAssignment.id (platform-assigned) OR TenantIntegration.id (custom). */
  reranker_integration_id: string | null;
  /** v2.2.31 — when false, bot RAG paths skip rerank. */
  reranker_apply_bot: boolean;
  /** v2.2.31 — when false, agent-assist /retrieve skips rerank. */
  reranker_apply_assist: boolean;
}

const defaultTenantRagSettings: TenantRagSettings = {
  reranker_enabled: false,
  reranker_provider: 'off',
  reranker_integration_id: null,
  // v2.2.31 — default TRUE so existing tenants without these keys keep
  // current "apply everywhere" behavior. Tenant-admin flips off via UI.
  reranker_apply_bot: true,
  reranker_apply_assist: true,
};

const defaultPlatformEmbeddingSettings: PlatformEmbeddingSettings = {
  // Embedding Model - Vertex AI gemini-embedding-001 (3072 native)
  embedding_provider: 'vertex_ai',
  embedding_model: 'gemini-embedding-001',
  embedding_location: 'us-central1',
  embedding_dimensions: 3072,
  // Task Types
  task_type_document: 'RETRIEVAL_DOCUMENT',
  task_type_query: 'RETRIEVAL_QUERY',
  // Processing Settings
  batch_size: 100,
  max_text_length: 8192,
  // Chunking Settings
  chunk_size_tokens: 512,
  chunk_overlap_tokens: 50,
  chunk_min_length: 50,
  // Search Settings
  search_top_k: 10,
  search_min_score: 0.5,
  context_max_tokens: 4000,
};

// ==================== Service ====================

export const settingsService = {
  // ==================== Platform Settings ====================

  /**
   * Get platform settings (super admin)
   * Does a deep merge to preserve nested defaults like ai.availableModels
   */
  async getPlatformSettings(): Promise<PlatformSettings> {
    try {
      const response = await api.get('/api/v1/settings/platform');
      const data = response.data || {};

      // Deep merge to preserve nested defaults
      return {
        ...defaultPlatformSettings,
        ...data,
        ai: {
          ...defaultPlatformSettings.ai,
          ...(data.ai || {}),
          // Use backend's availableModels if provided, otherwise use defaults
          availableModels: data.ai?.availableModels?.length > 0
            ? data.ai.availableModels
            : defaultPlatformSettings.ai.availableModels,
        },
        stt: {
          ...defaultPlatformSettings.stt,
          ...(data.stt || {}),
          // Use backend's availableEngines if provided, otherwise use defaults
          availableEngines: data.stt?.availableEngines?.length > 0
            ? data.stt.availableEngines
            : defaultPlatformSettings.stt.availableEngines,
        },
        flowArchitect: {
          ...defaultPlatformSettings.flowArchitect,
          ...(data.flowArchitect || {}),
        },
      };
    } catch {
      return defaultPlatformSettings;
    }
  },

  /**
   * Save platform settings (super admin)
   */
  async savePlatformSettings(settings: PlatformSettings): Promise<PlatformSettings> {
    const response = await api.put('/api/v1/settings/platform', settings);
    return response.data;
  },

  /**
   * Get default platform settings
   */
  getDefaultPlatformSettings(): PlatformSettings {
    return { ...defaultPlatformSettings };
  },

  /**
   * Get platform MFA settings (super admin only)
   */
  async getPlatformMfaSettings(): Promise<PlatformMfaSettings> {
    try {
      const response = await api.get('/api/v1/settings/platform/mfa');
      return { ...defaultPlatformMfaSettings, ...response.data };
    } catch {
      return defaultPlatformMfaSettings;
    }
  },

  /**
   * Save platform MFA settings (super admin only)
   */
  async savePlatformMfaSettings(settings: PlatformMfaSettings): Promise<PlatformMfaSettings> {
    const response = await api.put('/api/v1/settings/platform/mfa', settings);
    return response.data;
  },

  /**
   * Get default platform MFA settings
   */
  getDefaultPlatformMfaSettings(): PlatformMfaSettings {
    return { ...defaultPlatformMfaSettings };
  },

  /**
   * Get platform embedding settings (super admin only)
   * Settings for RAG: embedding model, chunking, and search parameters
   */
  async getPlatformEmbeddingSettings(): Promise<PlatformEmbeddingSettings> {
    try {
      const response = await api.get('/api/v1/platform/embedding-settings');
      return { ...defaultPlatformEmbeddingSettings, ...response.data };
    } catch {
      return defaultPlatformEmbeddingSettings;
    }
  },

  /**
   * Save platform embedding settings (super admin only)
   */
  async savePlatformEmbeddingSettings(settings: Partial<PlatformEmbeddingSettings>): Promise<PlatformEmbeddingSettings> {
    const response = await api.put('/api/v1/platform/embedding-settings', settings);
    return response.data;
  },

  /**
   * Get default platform embedding settings
   */
  getDefaultPlatformEmbeddingSettings(): PlatformEmbeddingSettings {
    return { ...defaultPlatformEmbeddingSettings };
  },

  /**
   * S-00 — Get platform RAG search settings (super admin only).
   * Controls reranker / MMR / hybrid / parent-doc behavior platform-wide.
   */
  async getPlatformRagSearchSettings(): Promise<PlatformRagSearchSettings> {
    try {
      const response = await api.get('/api/v1/platform/rag-search-settings');
      return { ...defaultPlatformRagSearchSettings, ...response.data };
    } catch {
      return defaultPlatformRagSearchSettings;
    }
  },

  /**
   * S-00 — Save platform RAG search settings (super admin only).
   */
  async savePlatformRagSearchSettings(
    settings: Partial<PlatformRagSearchSettings>,
  ): Promise<PlatformRagSearchSettings> {
    const response = await api.put('/api/v1/platform/rag-search-settings', settings);
    return response.data;
  },

  /**
   * S-00 — Get default platform RAG search settings.
   */
  getDefaultPlatformRagSearchSettings(): PlatformRagSearchSettings {
    return { ...defaultPlatformRagSearchSettings };
  },

  /**
   * S-04 — Read per-tenant RAG settings.
   *
   * Reads the tenant row (own or specified) and pulls `settings.rag.*`,
   * filling defaults for any missing keys.
   */
  async getTenantRagSettings(tenantId?: string): Promise<TenantRagSettings> {
    try {
      const path = tenantId ? `/api/v1/tenants/${tenantId}` : '/api/v1/tenants/me';
      const response = await api.get(path);
      const rag = response.data?.settings?.rag || {};
      return { ...defaultTenantRagSettings, ...rag };
    } catch {
      return { ...defaultTenantRagSettings };
    }
  },

  /**
   * S-04 — Write per-tenant RAG settings via the deep-merge PATCH routes.
   *
   * Role split is enforced server-side:
   *   * `/me/settings` (tenant-admin) accepts provider + integration_id;
   *     silently drops `reranker_enabled`.
   *   * `/{tenantId}/settings` (super-admin) accepts `reranker_enabled`;
   *     silently drops provider + integration_id.
   * Pass only the keys the caller's role can set.
   */
  async updateTenantRagSettings(
    rag: Partial<TenantRagSettings>,
    tenantId?: string,
  ): Promise<TenantRagSettings> {
    const path = tenantId
      ? `/api/v1/tenants/${tenantId}/settings`
      : '/api/v1/tenants/me/settings';
    const response = await api.patch(path, { rag });
    const merged = response.data?.settings?.rag || {};
    return { ...defaultTenantRagSettings, ...merged };
  },

  /**
   * S-04 — Default per-tenant RAG settings (reranker off).
   */
  getDefaultTenantRagSettings(): TenantRagSettings {
    return { ...defaultTenantRagSettings };
  },

  /**
   * Get documentation URL (for use in all portals)
   */
  async getDocumentationUrl(): Promise<string> {
    try {
      const response = await api.get('/api/v1/settings/platform/documentation-url');
      return response.data?.documentationUrl || '';
    } catch {
      return '';
    }
  },

  // ==================== Tenant Settings ====================

  /**
   * Get tenant settings (tenant admin)
   */
  async getTenantSettings(tenantId?: string): Promise<TenantSettings> {
    try {
      const params = tenantId ? { tenant_id: tenantId } : {};
      const response = await api.get('/api/v1/settings/tenant', { params });
      return { ...defaultTenantSettings, ...response.data };
    } catch {
      return defaultTenantSettings;
    }
  },

  /**
   * Save tenant settings (tenant admin)
   */
  async saveTenantSettings(settings: TenantSettings, tenantId?: string): Promise<TenantSettings> {
    const params = tenantId ? { tenant_id: tenantId } : {};
    const response = await api.put('/api/v1/settings/tenant', settings, { params });
    return response.data;
  },

  /**
   * Get default tenant settings
   */
  getDefaultTenantSettings(): TenantSettings {
    return { ...defaultTenantSettings };
  },

  /**
   * Get tenant DID settings (accessible to all users including officers)
   * This endpoint doesn't require settings.view permission
   */
  async getTenantDIDSettings(): Promise<TenantSettings['did']> {
    try {
      const response = await api.get('/api/v1/settings/tenant/did');
      return response.data?.did || defaultTenantSettings.did;
    } catch {
      return defaultTenantSettings.did;
    }
  },

  // ==================== Partner Settings ====================

  /**
   * Get partner settings (partner admin)
   */
  async getPartnerSettings(partnerId?: string): Promise<PartnerSettings> {
    try {
      const params = partnerId ? { partner_id: partnerId } : {};
      const response = await api.get('/api/v1/settings/partner', { params });
      return { ...defaultPartnerSettings, ...response.data };
    } catch {
      return defaultPartnerSettings;
    }
  },

  /**
   * Save partner settings (partner admin)
   */
  async savePartnerSettings(settings: PartnerSettings, partnerId?: string): Promise<PartnerSettings> {
    const params = partnerId ? { partner_id: partnerId } : {};
    const response = await api.put('/api/v1/settings/partner', settings, { params });
    return response.data;
  },

  /**
   * Get default partner settings
   */
  getDefaultPartnerSettings(): PartnerSettings {
    return { ...defaultPartnerSettings };
  },

  // ==================== IVR Profiles ====================

  /**
   * Get IVR profiles for a tenant
   */
  async getIvrProfiles(tenantId: string): Promise<IvrProfile[]> {
    try {
      const response = await api.get(`/api/v1/tenants/${tenantId}/ivr-profiles`);
      return response.data;
    } catch {
      return [];
    }
  },

  /**
   * Get a specific IVR profile
   */
  async getIvrProfile(tenantId: string, profileId: string): Promise<IvrProfile | null> {
    try {
      const response = await api.get(`/api/v1/tenants/${tenantId}/ivr-profiles/${profileId}`);
      return response.data;
    } catch {
      return null;
    }
  },

  /**
   * Create a new IVR profile
   */
  async createIvrProfile(tenantId: string, data: { name: string; description?: string; flow?: Record<string, unknown> }): Promise<IvrProfile> {
    const response = await api.post(`/api/v1/tenants/${tenantId}/ivr-profiles`, data);
    return response.data;
  },

  /**
   * Update an IVR profile
   */
  async updateIvrProfile(tenantId: string, profileId: string, data: Partial<IvrProfile>): Promise<IvrProfile> {
    const response = await api.put(`/api/v1/tenants/${tenantId}/ivr-profiles/${profileId}`, data);
    return response.data;
  },

  /**
   * Delete an IVR profile
   */
  async deleteIvrProfile(tenantId: string, profileId: string): Promise<void> {
    await api.delete(`/api/v1/tenants/${tenantId}/ivr-profiles/${profileId}`);
  },

  /**
   * Activate an IVR profile (deactivates all others for the tenant)
   */
  async activateIvrProfile(tenantId: string, profileId: string): Promise<void> {
    await api.post(`/api/v1/tenants/${tenantId}/ivr-profiles/${profileId}/activate`);
  },

  /**
   * Set an IVR profile as default
   */
  async setDefaultIvrProfile(tenantId: string, profileId: string): Promise<void> {
    await api.post(`/api/v1/tenants/${tenantId}/ivr-profiles/${profileId}/set-default`);
  },

  // ==================== Legacy methods (for backwards compatibility) ====================

  /**
   * @deprecated Use getPlatformSettings() instead
   */
  async getSettings(): Promise<PlatformSettings> {
    return this.getPlatformSettings();
  },

  /**
   * @deprecated Use savePlatformSettings() instead
   */
  async saveSettings(settings: PlatformSettings): Promise<{ saved: boolean; location: 'api' | 'local' }> {
    try {
      await this.savePlatformSettings(settings);
      return { saved: true, location: 'api' };
    } catch {
      return { saved: false, location: 'local' };
    }
  },

  /**
   * @deprecated Use getDefaultPlatformSettings() instead
   */
  getDefaultSettings(): PlatformSettings {
    return this.getDefaultPlatformSettings();
  },
};

export default settingsService;
